import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { useAuthStore } from '../store/useAuthStore';
import { CURRENCIES, DEFAULT_CURRENCY, currencyOptionLabel } from '../lib/currencies';
import { Globe, Save, Loader2, Settings2, ChevronDown, ArrowLeft, Coins } from 'lucide-react';

const TIMEZONES = [
  { group: 'Venezuela', options: [{ value: 'America/Caracas', label: 'Caracas (UTC-4)' }] },
  { group: 'Colombia / Perú / Ecuador', options: [
    { value: 'America/Bogota', label: 'Bogotá (UTC-5)' },
    { value: 'America/Lima', label: 'Lima (UTC-5)' },
    { value: 'America/Guayaquil', label: 'Guayaquil (UTC-5)' },
  ]},
  { group: 'México', options: [
    { value: 'America/Mexico_City', label: 'Ciudad de México (UTC-6)' },
    { value: 'America/Cancun', label: 'Cancún (UTC-5)' },
    { value: 'America/Tijuana', label: 'Tijuana (UTC-7)' },
  ]},
  { group: 'Cono Sur', options: [
    { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (UTC-3)' },
    { value: 'America/Santiago', label: 'Santiago (UTC-3/-4)' },
    { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)' },
    { value: 'America/Montevideo', label: 'Montevideo (UTC-3)' },
  ]},
  { group: 'Caribe y Centroamérica', options: [
    { value: 'America/Santo_Domingo', label: 'Santo Domingo (UTC-4)' },
    { value: 'America/Puerto_Rico', label: 'Puerto Rico (UTC-4)' },
    { value: 'America/Panama', label: 'Panamá (UTC-5)' },
    { value: 'America/Costa_Rica', label: 'Costa Rica (UTC-6)' },
  ]},
  { group: 'Estados Unidos', options: [
    { value: 'America/New_York', label: 'Nueva York (UTC-5/-4)' },
    { value: 'America/Chicago', label: 'Chicago (UTC-6/-5)' },
    { value: 'America/Denver', label: 'Denver (UTC-7/-6)' },
    { value: 'America/Los_Angeles', label: 'Los Ángeles (UTC-8/-7)' },
  ]},
  { group: 'Europa', options: [
    { value: 'Europe/Madrid', label: 'Madrid (UTC+1/+2)' },
    { value: 'Europe/London', label: 'Londres (UTC+0/+1)' },
  ]},
  { group: 'Universal', options: [{ value: 'UTC', label: 'UTC (Universal)' }] },
];

const ConfigPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timezone, setTimezone] = useState('America/Caracas');
  const [defaultCurrency, setDefaultCurrency] = useState(DEFAULT_CURRENCY);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    axios.get('/api/leads/settings')
      .then(r => {
        setTimezone(r.data.timezone ?? 'America/Caracas');
        setDefaultCurrency(r.data.default_currency ?? DEFAULT_CURRENCY);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.post('/api/leads/settings', { timezone, default_currency: defaultCurrency });
      // El tenant del store trae settings del login; sin refrescarlo, los
      // modales de negocio seguirían proponiendo la moneda anterior hasta
      // que el usuario vuelva a iniciar sesión.
      await useAuthStore.getState().checkAuth();
      setMessage({ type: 'success', text: 'Configuración guardada correctamente' });
    } catch {
      setMessage({ type: 'error', text: 'Error al guardar la configuración' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="animate-spin text-primary" size={36} />
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto space-y-6 animate-in fade-in duration-700">

      {/* Back button */}
      <button
        onClick={() => navigate('/settings')}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white transition-colors font-bold text-sm"
      >
        <ArrowLeft size={16} />
        Volver a Configuración
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 blur-[100px] -mr-32 -mt-32 rounded-full" />
        <div className="relative z-10 flex items-center gap-5">
          <div className="p-4 bg-primary rounded-2xl shadow-lg shadow-primary/20">
            <Settings2 className="text-white" size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              Configuración General
            </h1>
            <p className="text-slate-500 dark:text-gray-400 mt-1 font-medium text-sm">
              Ajustes globales del negocio
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`relative z-10 px-8 py-4 rounded-2xl flex items-center justify-center gap-3 font-black tracking-widest uppercase text-sm transition-all shadow-lg ${saving
            ? 'bg-slate-100 text-slate-400 dark:bg-gray-800 dark:text-gray-500 cursor-not-allowed'
            : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:scale-105 active:scale-95'
          }`}
        >
          {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>

      {/* Zona Horaria */}
      <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-500/10 rounded-lg text-blue-600 dark:text-blue-400">
            <Globe size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Zona Horaria del Negocio</h2>
            <p className="text-xs text-slate-500 dark:text-gray-400 font-medium">
              Usada por el agente de IA para calcular horarios de atención y fecha/hora local
            </p>
          </div>
        </div>
        <div className="relative">
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-bold appearance-none cursor-pointer"
          >
            {TIMEZONES.map(group => (
              <optgroup key={group.group} label={group.group}>
                {group.options.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
        </div>
      </div>

      {/* Moneda por defecto */}
      <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-600 dark:text-emerald-400">
            <Coins size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Moneda por Defecto</h2>
            <p className="text-xs text-slate-500 dark:text-gray-400 font-medium">
              Se preselecciona al crear un negocio. Cada negocio puede usar otra moneda si hace falta.
            </p>
          </div>
        </div>
        <div className="relative">
          <select
            value={defaultCurrency}
            onChange={(e) => setDefaultCurrency(e.target.value)}
            className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-bold appearance-none cursor-pointer"
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>{currencyOptionLabel(c)}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
        </div>
      </div>

      {/* Mensaje de estado */}
      {message.text && (
        <div className={`p-6 rounded-[2rem] text-center text-sm font-bold animate-in zoom-in duration-300 shadow-lg border ${message.type === 'success'
          ? 'bg-green-500 text-white border-green-600'
          : 'bg-red-500 text-white border-red-600'
        }`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default ConfigPage;
